import { Box, Card, CardContent, Grid2 as Grid, Typography, Stack, Chip } from "@mui/material";

import { RecordContextProvider, useListContext, EmailField, DateField } from "react-admin";

import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { StatusChipField } from "@/components/common/fields/StatusChipField";
import { getProps } from "@/utils/helpers/columnsHelper";
import { AvatarField } from "@/components/common/fields/AvatarField";
import { useNavigate } from "react-router";
import { ResourceName } from "@/types";

const ContactDetail = ({
  icon,
  children,
  color = "text.secondary",
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  color?: string;
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ color, display: "flex", alignItems: "center", minWidth: 20 }}>{icon}</Box>
    <Typography variant="body2" color={color} noWrap sx={{ flex: 1 }}>
      {children}
    </Typography>
  </Stack>
);

const DesktopGrid = () => {
  const { data } = useListContext();
  const navigate = useNavigate();

  if (!data) {
    return null;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {data.map((record) => (
          <Grid key={record.id} size={{ xs: 12, sm: 6, md: 4, xl: 3 }}>
            <RecordContextProvider value={record}>
              <Card
                component="div"
                onClick={() => {
                  navigate(`/clients/${record.id}/show`);
                }}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                    borderColor: "primary.light",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Header with Avatar and Name */}
                  <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        "& .MuiStack-root": {
                          "& .MuiTypography-root": { display: "none" },
                        },
                      }}
                    >
                      <AvatarField nameSource="client_legal_name" size={56} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          lineHeight: 1.2,
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {record.client_legal_name || record.client_short_name}
                      </Typography>

                      {/* Status and Key Badges */}
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        <StatusChipField {...getProps("client_status", ResourceName.CLIENTS)} />
                        {record.has_active_policy && (
                          <Chip
                            label="Active Policy"
                            size="small"
                            color="success"
                            variant="filled"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Contact Information */}
                  <Stack spacing={1.5}>
                    {record.client_contact_name && (
                      <ContactDetail icon={<BusinessIcon fontSize="small" />}>
                        {record.client_contact_name}
                      </ContactDetail>
                    )}

                    {record.client_email && (
                      <ContactDetail icon={<EmailIcon fontSize="small" />}>
                        <EmailField source="client_email" />
                      </ContactDetail>
                    )}

                    {record.client_phone_no && (
                      <ContactDetail icon={<PhoneIcon fontSize="small" />}>{record.client_phone_no}</ContactDetail>
                    )}

                    {record.client_state_headquartered && (
                      <ContactDetail icon={<LocationOnIcon fontSize="small" />}>
                        {record.client_state_headquartered}
                      </ContactDetail>
                    )}
                  </Stack>

                  {/* Footer with additional info */}
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {record.modified_datetime && (
                      <Typography variant="caption" color="text.secondary">
                        Updated <DateField source="modified_datetime" showTime={false} />
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </RecordContextProvider>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DesktopGrid;
